import { Box, Flex, Grid, GridItem, Icon, Text } from '@chakra-ui/react';
import { HiArrowDown, HiOutlineDownload } from 'react-icons/hi';

import dynamic from 'next/dynamic';

import { FadeIn } from '$components/ui/fade-in';

// Globe (cobe + WebGL) loaded only on the client and code-split so it
// doesn't bloat the initial bundle or break SSR.
const Globe = dynamic(() => import('$components/ui/globe'), {
  ssr: false,
  loading: () => null,
});
import { GridPattern } from '$components/ui/grid-pattern';
import { HEADER_HEIGHT_TOP } from '$components/layout/header';
import { social } from '$shared/social';
import { scrollToElement } from '$utils/scroll-to-element';
import { getTechIcon } from '$utils/tech-icon';

interface WelcomeProps {
  techs: string[];
}

const FALLBACK_TECHS = [
  'TypeScript',
  'Next.js',
  'React Native',
  'Kotlin',
  'Nest.js',
  'PostgreSQL',
];

export function Welcome({ techs }: WelcomeProps) {
  const list = techs.length > 0 ? techs : FALLBACK_TECHS;

  return (
    <Box
      as="section"
      pos="relative"
      w="100vw"
      ml="calc(50% - 50vw)"
      overflow="hidden"
      bg="bg.canvas"
      color="text.primary"
      minH={{ base: 'auto', md: '100vh' }}
      display="flex"
      alignItems="center"
      pt={{ base: `calc(${HEADER_HEIGHT_TOP} + 32px)`, md: HEADER_HEIGHT_TOP }}
      pb={{ base: '64px', md: '80px' }}
      px={{ base: '24px', md: '48px', lg: '80px' }}
    >
      <GridPattern />

      <Box
        pos="absolute"
        left="0"
        right="0"
        bottom="0"
        h={{ base: '120px', md: '180px' }}
        pointerEvents="none"
        zIndex="0"
        sx={{
          background:
            'linear-gradient(to bottom, transparent 0%, var(--chakra-colors-bg-canvas) 100%)',
        }}
      />

      <Grid
        pos="relative"
        zIndex="1"
        w="full"
        maxW="1280px"
        mx="auto"
        templateColumns={{ base: '1fr', md: '1.2fr 1fr' }}
        gap={{ base: '48px', md: '64px' }}
        alignItems="center"
      >
        <GridItem>
          <FadeIn delay={60}>
            <Text
              fontFamily="display"
              fontWeight="500"
              fontSize={{ base: '16px', md: '18px' }}
              color="text.muted"
              mb="8px"
            >
              Olá, sou
            </Text>
          </FadeIn>

          <FadeIn delay={120}>
            <Box
              as="h1"
              fontFamily="display"
              fontWeight="800"
              fontSize={{
                base: '56px',
                sm: '72px',
                md: '88px',
                lg: '104px',
              }}
              lineHeight="0.95"
              letterSpacing="-0.045em"
              color="text.primary"
              mb="24px"
            >
              <Box as="span" display="block">
                Silvano
              </Box>
              <Box as="span" display="block">
                Marques
                <Box as="span" color="accent.500">
                  .
                </Box>
              </Box>
            </Box>
          </FadeIn>

          <FadeIn delay={220}>
            <Text
              fontSize={{ base: '16px', md: '18px' }}
              color="text.muted"
              maxW="52ch"
              mb="36px"
              lineHeight="1.65"
            >
              Desenvolvedor Fullstack Pleno, construo aplicações completas para{' '}
              <Box as="span" color="accent.500" fontWeight="600">
                web
              </Box>
              ,{' '}
              <Box as="span" color="accent.500" fontWeight="600">
                mobile
              </Box>{' '}
              e{' '}
              <Box as="span" color="accent.500" fontWeight="600">
                desktop
              </Box>
              , da camada de UI à arquitetura com foco em código durável e
              experiência cuidada.
            </Text>
          </FadeIn>

          <FadeIn delay={320}>
            <Flex gap="12px" wrap="wrap" mb="28px">
              <Box
                as="a"
                href={social.resume}
                target="_blank"
                rel="noreferrer"
                display="inline-flex"
                alignItems="center"
                gap="10px"
                h="50px"
                px="24px"
                borderRadius="999px"
                bg="accent.500"
                color="bg.canvas"
                _dark={{ color: 'white' }}
                borderWidth="1px"
                borderColor="accent.500"
                fontFamily="body"
                fontSize="14px"
                fontWeight="500"
                transition="background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease"
                _hover={{
                  transform: 'translateY(-2px)',
                  bg: 'accent.600',
                  borderColor: 'accent.600',
                  boxShadow:
                    '0 14px 28px -10px var(--chakra-colors-accent-glow)',
                }}
              >
                Baixar currículo
                <Icon as={HiOutlineDownload} />
              </Box>

              <Box
                as="button"
                onClick={() => scrollToElement('projects')}
                display="inline-flex"
                alignItems="center"
                gap="10px"
                h="50px"
                px="24px"
                borderRadius="999px"
                bg="transparent"
                color="accent.500"
                borderWidth="1px"
                borderColor="accent.500"
                fontFamily="body"
                fontSize="14px"
                fontWeight="500"
                transition="background 0.2s ease, color 0.2s ease, transform 0.2s ease"
                _hover={{
                  transform: 'translateY(-2px)',
                  bg: 'accent.dim',
                }}
              >
                Ver projetos
                <Icon as={HiArrowDown} />
              </Box>
            </Flex>
          </FadeIn>

          <FadeIn delay={380}>
            <Box>
              <Text
                fontFamily="mono"
                fontSize="11px"
                letterSpacing="0.18em"
                textTransform="uppercase"
                color="text.faint"
                mb="12px"
              >
                Tecnologias
              </Text>
              <Flex gap="8px" wrap="wrap">
                {list.map((tech) => {
                  const TechIcon = getTechIcon(tech);
                  return (
                    <Flex
                      key={tech}
                      align="center"
                      gap="8px"
                      fontFamily="mono"
                      fontSize="12px"
                      letterSpacing="0.02em"
                      color="text.primary"
                      px="12px"
                      py="6px"
                      borderRadius="999px"
                      bg="bg.surface"
                      borderWidth="1px"
                      borderColor="line.subtle"
                      transition="border-color 0.2s ease, color 0.2s ease"
                      _hover={{
                        borderColor: 'accent.line',
                        color: 'accent.500',
                      }}
                    >
                      <Icon as={TechIcon} fontSize="14px" />
                      {tech}
                    </Flex>
                  );
                })}
              </Flex>
            </Box>
          </FadeIn>
        </GridItem>

        <GridItem
          display="flex"
          justifyContent={{ base: 'center', md: 'flex-end' }}
          alignItems="center"
        >
          <FadeIn delay={200}>
            <Box
              w={{ base: '260px', sm: '340px', md: '420px', lg: '480px' }}
              h={{ base: '260px', sm: '340px', md: '420px', lg: '480px' }}
            >
              <Globe />
            </Box>
          </FadeIn>
        </GridItem>
      </Grid>
    </Box>
  );
}
